// ------------------------------------------------------------------------ //
// TanStack Query hooks for Users
// ------------------------------------------------------------------------ //
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api/api';
import { useState } from 'react';

// ------------------------------------------------------------------------ //
// Query Keys
// ------------------------------------------------------------------------ //
export const userKeys = {
  all: ['users'],
  active: () => [...userKeys.all, 'active'],
  connected: () => [...userKeys.all, 'connected'],
  info: (userId) => [...userKeys.all, 'info', userId],
};

// ------------------------------------------------------------------------ //
// Get Active Users
// ------------------------------------------------------------------------ //
export function useActiveUsers() {
  return useQuery({
    queryKey: userKeys.active(),
    queryFn: api.getActiveUsers,
    staleTime: 5000,
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });
}

// ------------------------------------------------------------------------ //
// Get Connected Users (grouped by activity/section)
// ------------------------------------------------------------------------ //
export function useConnectedUsers() {
  return useQuery({
    queryKey: userKeys.connected(),
    queryFn: api.getConnectedUsers,
    staleTime: 5000,
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });
}

// ------------------------------------------------------------------------ //
// Get User Info (for modal display)
// ------------------------------------------------------------------------ //
export function useUserInfo(userId) {
  return useQuery({
    queryKey: userKeys.info(userId),
    queryFn: () => api.getUserInfo(userId),
    enabled: !!userId,
    staleTime: 30000,
  });
}

// ------------------------------------------------------------------------ //
// Update User Availability (Mutation)
// ------------------------------------------------------------------------ //
export function useUpdateUserAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, available, unavailableStartedAt, unavailableEndedAt }) =>
      api.putUserInfo({
        userId,
        available,
        unavailableStartedAt,
        unavailableEndedAt,
      }),
    onSuccess: (data, variables) => {
      // Invalidate user info queries to trigger refetch
      queryClient.invalidateQueries(userKeys.info(variables.userId));
    },
    retry: 1,
  });
}

// ------------------------------------------------------------------------ //
// Update User Unavailability End Time (Mutation)
// ------------------------------------------------------------------------ //
export function useUpdateUnavailabilityEndTime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, unavailableEndedAt }) =>
      api.putUserInfo({
        userId,
        unavailableEndedAt,
      }),
    onSuccess: (data, variables) => {
      // Invalidate user info queries to trigger refetch
      queryClient.invalidateQueries(userKeys.info(variables.userId));
    },
    retry: 1,
  });
}

// ------------------------------------------------------------------------ //
// Hook to manage user info modal state
// This replaces the Redux showUserInfo/hideUserInfo actions
// ------------------------------------------------------------------------ //
export function useUserInfoModal() {

  const [selectedUserId, setSelectedUserId] = useState(null);
  const { data: userInfo, isLoading } = useUserInfo(selectedUserId);

  const showUserInfo = (userId) => {
    setSelectedUserId(userId);
  };

  const hideUserInfo = () => {
    setSelectedUserId(null);
  };

  return {
    userInfo: selectedUserId ? userInfo : null,
    isLoading,
    showUserInfo,
    hideUserInfo,
  };
}
